import { useState, useCallback } from "react";

import { API } from "../lib/api";

export type AccountType = "domaine" | "resto";

export interface FeederDomaine {
  philosophie: string[];
  vinification: string[];
  elevage: string[];
  style: string[];
  instructions?: string;
}
export interface FeederResto { menu: string; instructions?: string; }
export interface Feeder {
  accountType: AccountType;
  domaine: FeederDomaine;
  resto: FeederResto;
}

export interface Brand {
  logo: string | null;
  bgColor: string;
  textColor: string;
  cardColor: string;
  feeder: Feeder;
}

const DEFAULT_FEEDER: Feeder = {
  accountType: "domaine",
  domaine: { philosophie: [], vinification: [], elevage: [], style: [] },
  resto: { menu: "" },
};

const DEFAULT_BRAND: Brand = {
  logo: null,
  bgColor: "#0A0908",
  textColor: "#F0EDE8",
  cardColor: "#141210",
  feeder: DEFAULT_FEEDER,
};

function mergeFeeder(f?: Partial<Feeder>): Feeder {
  return {
    ...DEFAULT_FEEDER,
    ...(f || {}),
    domaine: { ...DEFAULT_FEEDER.domaine, ...((f && f.domaine) || {}) },
    resto: { ...DEFAULT_FEEDER.resto, ...((f && f.resto) || {}) },
  };
}

export function useBrand() {
  const [brand, setBrandState] = useState<Brand>(DEFAULT_BRAND);

  const fetchBrand = useCallback(async () => {
    const res = await fetch(`${API}/api/brand`);
    const data = await res.json();
    setBrandState({ ...DEFAULT_BRAND, ...data, feeder: mergeFeeder(data.feeder) });
  }, []);

  const updateBrand = useCallback(async (brand: Brand) => {
    const res = await fetch(`${API}/api/brand`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(brand),
    });
    const data = await res.json();
    setBrandState({ ...DEFAULT_BRAND, ...data, feeder: mergeFeeder(data.feeder) });
  }, []);

  const getAdminEmail = useCallback(async (): Promise<string> => {
    const res = await fetch(`${API}/api/brand/admin-email`);
    const data = await res.json();
    return data.email;
  }, []);

  const setAdminEmail = useCallback(async (email: string) => {
    await fetch(`${API}/api/brand/admin-email`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  }, []);

  return { brand, setBrand: setBrandState, fetchBrand, updateBrand, getAdminEmail, setAdminEmail };
}
